import { action, computed, observable, makeObservable } from 'mobx';
import { Canceler } from 'axios';
import { defaultPagination } from 'src/constants/main';
import { IPagination } from 'src/types/main';
import { IBranch, IBranchFromSocket } from 'src/types/branches';
import { IBranchesFilters } from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/types/branches';
import client from 'src/services/client';
import { defaultBranchesFilters } from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/constants/branches';
import { arrayMerge } from 'src/utils/formatter';
import { getCancelToken } from 'src/utils/helpers';
import { getBranchesAvailabilityParams } from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/helpers/branches';
import logger from '~/services/logger';
import { SCREENS } from '~/constants/screens';

class BranchesStore {
  constructor() {
    makeObservable(this);
  }

  @observable pagination: IPagination<IBranch> = defaultPagination;
  @observable filters: IBranchesFilters = defaultBranchesFilters;

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  /**
   * Are there no existed branches in system
   */
  get isEmptyBranchesList() {
    return !this.filters.search && !this.filters.availability && !this.pagination.loading && !this.pagination.data.length;
  }

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action setFilters = (filters: Partial<IBranchesFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this._fetchBranchesList({ page: 1 });
  };

  public fetchBranchesList = async (page?: number) => {
    await this._fetchBranchesList({ page });
  };
  @action private _fetchBranchesList = async (param: { page?: number; initLoading?: boolean }) => {
    try {
      const cancelId = 'fetchBranches';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      const { page = this.pagination.current_page || 1, initLoading = true } = param;

      // skip loading if its already initialized
      if (initLoading) {
        this.pagination.loading = true;
        this.pagination.refreshing = true;
      }

      const pagination = await client.get('v4/branches', {
        params: {
          page,
          is_delete: 0,
          sort_field: this.filters.sortField,
          sort_dir: this.filters.sortDir,
          name: this.filters.search,
          ...getBranchesAvailabilityParams(this.filters.availability),
        },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.loading = false;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'branchesStore fetchBranchesList' });
    }
  };

  /**
   * Update statuses for branch in real time
   *
   * @param updatedBranch data from socket
   */
  @action handleBranchUpdatesBySocket = (updatedBranch: IBranchFromSocket) => {
    const { status, is_delete, created_at } = updatedBranch;

    const updatedBranchFromList = this.pagination.data.find((_branch) => _branch.id === updatedBranch.id);

    // if branch availability that affects representation of the list is changed then we fetch a list with fresh updates,
    // otherwise we update the list manually
    if (status !== undefined || is_delete !== undefined || created_at) {
      // fetch updated list silently
      this._fetchBranchesList({ initLoading: false });
    } else {
      // skip updating manually if updated branch is not in the list
      if (!updatedBranchFromList) return;

      // update branch data (name, delivery_type, payment_methods, etc.)
      this.pagination.data = this.pagination.data.map((_branch) => {
        if (_branch.id !== updatedBranch.id) return _branch;
        return { ..._branch, ...updatedBranch };
      });
    }
  };

  /**
   * Update branch busy and availability statuses
   *
   * @param id
   * @param params
   */
  @action updateBranch = async (id: number, params: Partial<IBranch>) => {
    try {
      const branch = await client.patch(`v4/branches/${id}`, params);
      this.pagination = { ...this.pagination, data: arrayMerge(this.pagination.data, [branch], 'id') };
    } catch (error: any) {
      logger.error({ error, place: 'branchesStore updateBranch' });
    }
  };

  @action supposedBusyExpire = async (id: number) => {
    try {
      this.pagination.refreshing = true;
      const branchExpire: { msg: string } = await client.get(`v3/branches/${id}/busy-expire`);
      return branchExpire.msg;
    } catch (error: any) {
      return error.message;
    } finally {
      this.pagination.refreshing = false;
    }
  };

  @action restoreBranch = async (id: number) => {
    try {
      this.pagination.refreshing = true;
      await client.patch(`v4/branches/${id}/restore`);
      // fetch updated list
      this._fetchBranchesList({ initLoading: false });
    } catch (error: any) {
      logger.error({ error, place: 'branchesStore restoreBranch' });
    }
  };

  @action deleteBranch = async (id: number) => {
    try {
      this.pagination.refreshing = true;
      await client.delete(`v4/branches/${id}`);
      // fetch updated list
      this._fetchBranchesList({ initLoading: false });
    } catch (error: any) {
      this.pagination.refreshing = false;

      // handle POS sync branch error
      logger.error({ error, place: 'branchesStore deleteBranch', showModal: true });
    }
  };

  clear = () => {
    if (!window.location.pathname.includes(SCREENS.BRANCHES())) {
      this.pagination = defaultPagination;
      this.filters = defaultBranchesFilters;
    }
  };
}

const branchesStore = new BranchesStore();

export default branchesStore;
